/** Sale Desk Print Helper — browser client */

export const PRINT_HELPER_VERSION = "1.2.0";
export const PRINT_HELPER_BASE = "http://127.0.0.1:19100";
/** Versioned filename so browsers/CDN never serve a stale broken build. */
export const PRINT_HELPER_FILENAME = `SaleDesk-Print-Helper-v${PRINT_HELPER_VERSION}.exe`;
export const PRINT_HELPER_DOWNLOAD_URL = `/downloads/${PRINT_HELPER_FILENAME}?v=${PRINT_HELPER_VERSION}`;

export type HelperPrinter = { name: string; status?: string; port?: string };

export type HelperPing = {
  ok: boolean;
  version?: string;
  engine?: string;
  name?: string;
};

function versionParts(v: string): number[] {
  return String(v)
    .split(".")
    .map((n) => parseInt(n, 10) || 0);
}

/** true if installed helper version >= required (e.g. 1.1.0). */
export function isHelperVersionOk(
  version: string | undefined,
  required = PRINT_HELPER_VERSION
): boolean {
  if (!version) return false;
  const a = versionParts(version);
  const b = versionParts(required);
  for (let i = 0; i < 3; i++) {
    const x = a[i] || 0;
    const y = b[i] || 0;
    if (x > y) return true;
    if (x < y) return false;
  }
  return true;
}

export async function pingPrintHelper(
  timeoutMs = 1500
): Promise<HelperPing | null> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(`${PRINT_HELPER_BASE}/ping`, { signal: ctrl.signal });
    if (!res.ok) return null;
    const data = (await res.json()) as HelperPing;
    return data?.ok ? data : null;
  } catch {
    return null;
  } finally {
    clearTimeout(t);
  }
}

export async function listHelperPrinters(): Promise<HelperPrinter[]> {
  const res = await fetch(`${PRINT_HELPER_BASE}/printers`);
  if (!res.ok) throw new Error("Could not list printers from Sale Desk Print Helper.");
  const data = await res.json();
  if (!data?.ok) throw new Error(data?.error || "Helper returned an error.");
  return (data.printers || []) as HelperPrinter[];
}

export async function printViaHelper(
  printerName: string,
  bytes: Uint8Array
): Promise<void> {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  const dataBase64 = btoa(binary);

  const res = await fetch(`${PRINT_HELPER_BASE}/print`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ printer: printerName, dataBase64 }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.ok) {
    throw new Error(data?.error || "Print Helper failed to send the job.");
  }
}

export function downloadPrintHelperInstaller() {
  const a = document.createElement("a");
  a.href = PRINT_HELPER_DOWNLOAD_URL;
  a.download = PRINT_HELPER_FILENAME;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
